import type { Ref, JSX } from 'preact';
import { forwardRef } from 'preact/compat';
import { useSwitchHandlers } from './useSwitchHandlers';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import { StyledSwitchVariantOptions, styles } from './themes/StyledSwitchStyles.css';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { UnpackSignals } from '#utils/UNSAFE_attributeUtils';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { StyledSwitchRedwoodTheme } from './themes/redwood/StyledSwitchTheme';
import { classNames } from '#utils/UNSAFE_classNames';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { merge } from '#utils/UNSAFE_stringUtils';
import { useId } from '#hooks/UNSAFE_useId';
import { useActive } from '#hooks/UNSAFE_useActive';
import { mergeProps } from '#utils/UNSAFE_mergeProps/mergeProps';

/**
 * Accessibility: You must provide at least one of 'aria-label' or 'aria-labelledby' for this control.
 * - `aria-label`: Defines a string that labels the switch for assistive technology users. See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label
 * - `aria-labelledby`: References element(s) whose text content labels the switch. See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-labelledby
 *
 * Both may be provided, but one or the other is required for compliance.
 *
 * @property aria-label       Defines a string value that labels the switch for accessibility. Required unless `aria-labelledby` is present.
 * @property aria-labelledby  References element(s) that provide a label for the switch. Required unless `aria-label` is present.
 * @property aria-describedby (Optional) References the element(s) that describe the object.
 */
type AriaProps = Pick<
  UnpackSignals<JSX.AriaAttributes>,
  'aria-describedby' | 'aria-invalid' | 'aria-label' | 'aria-labelledby'
>;

/**
 * Utility type: enforce at least one of 'aria-label' or 'aria-labelledby' using AriaProps.
 * Note: Due to an existing Typescript issue when a component has forwardRef, this discriminated type
 * is not enforced. Keeping it here in case it is fixed someday.
 */
type RequireAriaLabelOrLabelledby<
  T extends {
    'aria-label'?: AriaProps['aria-label'];
    'aria-labelledby'?: AriaProps['aria-labelledby'];
  }
> =
  | (T & { 'aria-label': NonNullable<T['aria-label']>; 'aria-labelledby'?: T['aria-labelledby'] })
  | (T & { 'aria-label'?: T['aria-label']; 'aria-labelledby': NonNullable<T['aria-labelledby']> });

type Props = TestIdProps &
  RequireAriaLabelOrLabelledby<AriaProps> & {
    /**
     * The baseId to use to create sub-ids, if needed. For example, StyledSwitch creates a HiddenAccessible
     * for 'read only' text as a workaround for aria-readonly not reading 'readonly' on Jaws or Voiceover.
     */
    baseId?: string;
    /**
     * Specifies whether the switch is checked (on) or not (off).
     */
    isChecked?: boolean;
    /**
     * Specifies if the switch is disabled and cannot be interacted with.
     */
    isDisabled?: boolean;
    /**
     * Specifies if the switch is readonly and its value cannot be changed by user interaction.
     */
    isReadonly?: boolean;
    /**
     * Text shown when the switch is readonly and checked.
     */
    readonlyOnText: string;
    /**
     * Text shown when the switch is readonly and not checked.
     */
    readonlyOffText: string;
    /**
     * Triggered when the switch state changes due to user interaction.
     */
    onChange?: (event: MouseEvent | KeyboardEvent) => void;
  };

/**
 * StyledSwitch is a controlled component that displays a stylized binary switch (on/off).
 * It supports accessibility attributes and allows parent components to control its checked and disabled states.
 *
 * This component is implemented with `forwardRef` to allow parent components to obtain a ref
 * to the interactive switch element (track). This is important for programmatically managing focus.
 *
 * @example
 *   <StyledSwitch isChecked={checked} onChange={handleChange} ref={switchRef} />
 */

const StyledSwitch = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      baseId,
      isChecked = false,
      isDisabled,
      isReadonly,
      readonlyOnText,
      readonlyOffText,
      onChange,
      testId
    }: Props,
    ref: Ref<HTMLDivElement> = null
  ) => {
    const generatedBaseId = useId();
    const readonlyBaseId = baseId ?? generatedBaseId;
    const readonlyId = `${readonlyBaseId}-readonly`;
    // Prefer isDisabled over isReadonly:
    const resolvedDisabled = !!isDisabled;
    const resolvedReadonly = !resolvedDisabled && !!isReadonly;
    const mergedAriaDescribedBy = resolvedReadonly
      ? merge([ariaDescribedBy, readonlyId])
      : ariaDescribedBy;

    const ariaChecked = !!isChecked;
    const { tabbableModeProps } = useTabbableMode(resolvedDisabled);
    const { isActive, activeProps } = useActive({ isDisabled: isDisabled || isReadonly });

    const { classes: themeContainerClass } = useComponentTheme<StyledSwitchVariantOptions>(
      StyledSwitchRedwoodTheme,
      {
        target: 'container',
        checked: isChecked ? 'isChecked' : 'notChecked',
        disabled: resolvedDisabled ? 'isDisabled' : 'notDisabled',
        readonly: resolvedReadonly ? 'isReadonly' : 'notReadonly'
      }
    );
    const containerClass = classNames([styles.container, themeContainerClass]);

    const { classes: themeTrackClass } = useComponentTheme<StyledSwitchVariantOptions>(
      StyledSwitchRedwoodTheme,
      {
        target: 'track',
        checked: isChecked ? 'isChecked' : 'notChecked',
        disabled: resolvedDisabled ? 'isDisabled' : 'notDisabled',
        readonly: resolvedReadonly ? 'isReadonly' : 'notReadonly',
        active: isActive ? 'isActive' : 'notActive'
      }
    );
    const trackClass = classNames([styles.track, themeTrackClass]);

    const { classes: themeThumbClass } = useComponentTheme<StyledSwitchVariantOptions>(
      StyledSwitchRedwoodTheme,
      {
        target: 'thumb',
        checked: isChecked ? 'isChecked' : 'notChecked',
        disabled: resolvedDisabled ? 'isDisabled' : 'notDisabled',
        readonly: resolvedReadonly ? 'isReadonly' : 'notReadonly'
      }
    );
    const thumbClass = classNames([styles.thumb, themeThumbClass]);

    // Use the new hook for events
    const { onClick, onKeyDown, onKeyUp, onKeyPress } = useSwitchHandlers({
      isDisabled: resolvedDisabled,
      isReadonly: resolvedReadonly,
      onChange
    });

    let readonlyText: string | undefined = undefined;
    if (resolvedReadonly) {
      if (isChecked) {
        readonlyText = readonlyOnText;
      } else {
        readonlyText = readonlyOffText;
      }
    }

    const rootTestIdProps = useTestId(testId);
    const thumbTestIdProps = useTestId(testId ? testId + '_thumb' : undefined);

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
    const readonlyLabel = translations.formControl_readOnly();

    return (
      <div class={styles.root} {...rootTestIdProps}>
        <div class={containerClass}>
          <div
            role="switch"
            {...tabbableModeProps}
            class={trackClass}
            aria-checked={ariaChecked}
            aria-readonly={resolvedReadonly || undefined}
            aria-disabled={resolvedDisabled || undefined}
            aria-invalid={ariaInvalid}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={mergedAriaDescribedBy}
            ref={ref}
            {...mergeProps(activeProps, { onKeyUp, onKeyDown, onClick, onKeyPress })}>
            <div class={thumbClass} {...thumbTestIdProps}>
              {readonlyText}
            </div>
          </div>
        </div>
        {
          // JAWS and iOS VoiceOver do not currently announce 'readonly' from aria-readonly,
          // even though it is listed as supported for role="switch" per ARIA spec:
          // https://www.w3.org/TR/wai-aria-1.3/#switch
          // For now, we add a HiddenAccessible element as a workaround so screen readers will announce it.
          // See 
        }
        {resolvedReadonly && (
          <HiddenAccessible id={readonlyId} isHidden={true}>
            {readonlyLabel}
          </HiddenAccessible>
        )}
      </div>
    );
  }
);

export { StyledSwitch };
